import { BlockTypes } from "./block-types";
import { ConnectPointClickEvent } from "./connectors";
import { MouseEvent } from "react";

export const ROOT_BLOCK_ID = "root";

export const AUTO_SCALING_GROUP_SCALE_UP_THRESHOLD = 0.8
export const AUTO_SCALING_GROUP_SCALE_DOWN_THRESHOLD = 0.3
export const QUEUE_MAX_STORAGE = 120000;

export interface ScreenPosition {
  top: number;
  left: number;
}

export interface Block extends ScreenPosition {
  id: string;
  width: number;
  height: number;
  inputConnectors?: string[];
  outputConnectors?: string[];
  isSelected?: boolean;
  blockType: BlockTypes;
  inputCapacity: IOPS;
  processingCapacity: IOPS;
  // optional maximum processing capacity for blocks that can scale their processing (e.g., Cache)
  maxProcessingCapacity?: IOPS;
  dynamicProperties?: {
    [key: string]: any;
  };
  costPerCycle?: number;
}

export interface BlockPositionMap {
  [key: string]: Block;
}
export interface BlockProps extends Block {
  onConnectorClick: (event: ConnectPointClickEvent) => void;
  onMouseDown: (event: BlockMouseDownEvent) => void;
  onMouseUp: () => void;
  onDeleteClick: (e: MouseEvent<HTMLButtonElement>, blockID: string) => void;
  startBlock?: ConnectPointClickEvent | null;
  inputBlocks?: string[];
  outputBlocks?: string[];
  gameIsStarted: boolean;
}

export interface BlockMouseDownEvent {
  diff: ScreenPosition;
  blockID: string;
}

export enum OperationTypes {
  READ = "read",
  WRITE = "write",
}

export enum ContentTypes {
  STATIC = "static",
  DYNAMIC = "dynamic",
}

/*
  {
    total:10,
    byOperationType: {
      read: {
        total: 5,
        byContentType: {
          static: 2,
          dynamic: 3
        }
      },
      write: {
        total: 5,
        byContentType: {
          static: 0,
          dynamic: 5
        }
      }
    }
  }
*/

export interface ContentIOPSCountMap {
  [key: string]: number;
}

export interface ContentIOPS {
  total: number;
  byContentType: ContentIOPSCountMap;
}

export interface OperationTypeMap {
  [key: string]: ContentIOPS;
}

export interface IOPS {
  byOperationType: OperationTypeMap;
  total: number;
}

export interface IOPSCapacity extends IOPS {
  maxConcurrentIOPS: number;
}

export interface IOPSDistribution {
  readStatic: number;
  readDynamic: number;
  writeDynamic: number;
}

export interface BlockIOPS {
  blockID: string;
  inputIOPS: IOPS;
  outputIOPS: IOPS;
  percentageSuccess: number
}

export interface FlattendIOPS {
  total: number;
  [key: string]: number; // operation type as key, e.g., 'read', 'write'
}

export interface ConnectorLimitMap {
  [key: string]: number;
}

export const initialRootIOPS: IOPS = {
  total: 20,
  byOperationType: {
    [`${OperationTypes.READ}`]: {
      total: 10,
      byContentType: {
        [`${ContentTypes.DYNAMIC}`]: 5,
        [`${ContentTypes.STATIC}`]: 5,
      },
    },
    [`${OperationTypes.WRITE}`]: {
      total: 10,
      byContentType: { [`${ContentTypes.DYNAMIC}`]: 10 },
    },
  },
};

export function zeroIOPS(): IOPS {
  return {
    total: 0,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 0,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 0,
          [`${ContentTypes.STATIC}`]: 0,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: 0,
        byContentType: { [`${ContentTypes.DYNAMIC}`]: 0 },
      },
    }
  }
}

export function zeroIOPSCapacity(): IOPSCapacity {
  return {
    total: 0,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 0,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 0,
          [`${ContentTypes.STATIC}`]: 0,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: 0,
        byContentType: { [`${ContentTypes.DYNAMIC}`]: 0 },
      },
    },
    maxConcurrentIOPS: 0
  }
}


export interface BlockIOPSMap {
  [key: string]: BlockIOPS;
}

export interface BlockConfig {
  displayName: string;
  imagePath: string;
  isInitiator: boolean;
  isTerminator: boolean;
  isInterceptor: boolean;
  allowedInputTypes: Set<BlockTypes>;
  allowedOutputTypes: Set<BlockTypes>;
  inputCapacity: IOPS;
  processingCapacity: IOPS;
  // optional maximum processing capacity for blocks that support scaling
  maxProcessingCapacity?: IOPS;
  purchasePrice: number;
  sellPrice: number;
  costPerCycle: number;
  outputConnectorLimits: ConnectorLimitMap;
  blockSideEffects?: (current: Block, iops: IOPS) => Block;
}

export interface BlockConfigMap {
  [key: string]: BlockConfig;
}

export const blockConfigs: BlockConfigMap = {
  [`${BlockTypes.USER}`]: {
    displayName: "User",
    imagePath: "",
    isInitiator: false,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([]),
    allowedOutputTypes: new Set([
      BlockTypes.APP_SERVER,
      BlockTypes.LOAD_BALANCER,
      BlockTypes.LAMBDA,
      BlockTypes.AUTO_SCALING_GROUP,
      BlockTypes.CDN
    ]),
    inputCapacity: zeroIOPS(),
    processingCapacity: zeroIOPS(),
    purchasePrice: 0,
    costPerCycle: 0,
    sellPrice: 0,
    outputConnectorLimits: {
      [`${BlockTypes.APP_SERVER}`]: 3,
    },
  },
  [`${BlockTypes.APP_SERVER}`]: {
    displayName: "App Server",
    imagePath: "",
    isInitiator: true,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.USER, BlockTypes.LOAD_BALANCER, BlockTypes.LAMBDA, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.QUEUE]),
    allowedOutputTypes: new Set([BlockTypes.DATABASE, BlockTypes.DB_READ_REPLICA, BlockTypes.LAMBDA, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.QUEUE]),
    inputCapacity: {
      total: 1000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 600,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 300,
            [`${ContentTypes.STATIC}`]: 300,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: 400,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 400 },
        },
      },
    },

    processingCapacity: zeroIOPS(),
    purchasePrice: 100,
    sellPrice: 50,
    costPerCycle: 0.6,
    outputConnectorLimits: {}
  },
  [`${BlockTypes.AUTO_SCALING_GROUP}`]: {
    displayName: "Auto Scaling Group",
    imagePath: "",
    isInitiator: true,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.USER, BlockTypes.LOAD_BALANCER, BlockTypes.LAMBDA, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.QUEUE]),
    allowedOutputTypes: new Set([BlockTypes.DATABASE, BlockTypes.DB_READ_REPLICA, BlockTypes.LAMBDA, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.QUEUE]),
    inputCapacity: {
      total: 1000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 600,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 300,
            [`${ContentTypes.STATIC}`]: 300,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: 400,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 400 },
        },
      }
    },
    processingCapacity: zeroIOPS(),
    purchasePrice: 500,
    sellPrice: 50,
    costPerCycle: 0.6,
    outputConnectorLimits: {},
    blockSideEffects: (block: Block, iops: IOPS) => autoScalingGroupSideEffects(block, iops)
  },
  [`${BlockTypes.DATABASE}`]: {
    displayName: "Database",
    imagePath: "",
    isInitiator: true,
    isTerminator: false,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.APP_SERVER, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.CACHE, BlockTypes.LAMBDA]),
    allowedOutputTypes: new Set(),
    inputCapacity: {
      total: 12000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 11000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 1000,
            [`${ContentTypes.STATIC}`]: 10000,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total:  1000,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 1000 },
        },
      },
    },
    processingCapacity: {
      total: 12000,//12000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 11000,//11000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 1000,//1000,
            [`${ContentTypes.STATIC}`]: 10000,//10000,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: 1000,//1000,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 1000//1000
           },
        },
      },
    },
    purchasePrice: 150,
    sellPrice: 75,
    costPerCycle: 1.2,
    outputConnectorLimits: {}
  },
  [`${BlockTypes.DB_READ_REPLICA}`]: {
    displayName: "DB Read Replica",
    imagePath: "",
    isInitiator: true,
    isTerminator: false,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.APP_SERVER, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.CACHE, BlockTypes.LAMBDA]),
    allowedOutputTypes: new Set(),
    inputCapacity: {
      total: 15000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 15000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 3000,
            [`${ContentTypes.STATIC}`]: 12000,
          },
        }
      },
    },
    processingCapacity: {
      total: 15000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 15000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 3000,
            [`${ContentTypes.STATIC}`]: 12000,
          },
        }
      },
    },
    purchasePrice: 100,
    sellPrice: 50,
    costPerCycle: 0.8,
    outputConnectorLimits: {}
  },
  [`${BlockTypes.LOAD_BALANCER}`]: {
    displayName: "Load Balancer",
    imagePath: "",
    isInitiator: true,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.USER, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.APP_SERVER]),
    allowedOutputTypes: new Set([BlockTypes.APP_SERVER, BlockTypes.AUTO_SCALING_GROUP]),
    inputCapacity: {
      total: 300000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 200000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 100000,
            [`${ContentTypes.STATIC}`]: 100000,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: 100000,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 100000 },
        },
      },
    },
    processingCapacity: zeroIOPS(),
    purchasePrice: 50,
    sellPrice: 25,
    costPerCycle: 0.06,
    outputConnectorLimits: {}
  },
  [`${BlockTypes.CDN}`]: {
    displayName: "Content Delivery Network (CDN)",
    imagePath: "",
    isInitiator: true,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.USER]),
    allowedOutputTypes: new Set(),
    inputCapacity: {
      total: 10000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 10000,
          byContentType: {
            [`${ContentTypes.STATIC}`]: 10000,
          },
        }
      },
    },

    processingCapacity: {
      total: 10000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 10000,
          byContentType: {
            [`${ContentTypes.STATIC}`]: 10000,
          },
        }
      },
    },
    purchasePrice: 500,
    sellPrice: 250,
    costPerCycle: 2,
    outputConnectorLimits: {}
  },
  [`${BlockTypes.CACHE}`]: {
    displayName: "Cache",
    imagePath: "",
    isInitiator: true,
    isTerminator: false,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.APP_SERVER, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.LAMBDA]),
    allowedOutputTypes: new Set([BlockTypes.DATABASE, BlockTypes.DB_READ_REPLICA]),
    inputCapacity: {
      total: 12000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 11000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 1000,
            [`${ContentTypes.STATIC}`]: 10000,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: 1000,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 1000 },
        },
      },
    },
    processingCapacity: zeroIOPS(), // processing starts at zero and will scale up via cacheSideEffects
    maxProcessingCapacity: {
      total: 12000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 12000,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 2000,
            [`${ContentTypes.STATIC}`]: 10000,
          },
        }
      },
    },
    purchasePrice: 200,
    sellPrice: 100,
    costPerCycle: 1.0,
    outputConnectorLimits: {},
    // attach side effects to warm the cache's processing capacity
    blockSideEffects: (block: Block, iops: IOPS) => cacheSideEffects(block, iops)
  },
  [`${BlockTypes.LAMBDA}`]: {
    displayName: "Lambda",
    imagePath: "",
    isInitiator: true,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([BlockTypes.USER, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.LAMBDA, BlockTypes.QUEUE]),
    allowedOutputTypes: new Set([BlockTypes.APP_SERVER, BlockTypes.LAMBDA, BlockTypes.DB_READ_REPLICA, BlockTypes.DATABASE, BlockTypes.AUTO_SCALING_GROUP, BlockTypes.LOAD_BALANCER, BlockTypes.QUEUE]),
    inputCapacity: {
      total: 1000, //1000,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: 600, //600,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: 300,//300,
            [`${ContentTypes.STATIC}`]: 300,//300,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: 400,//400,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: 400,//400 },
        }
        },
      }
    },

    processingCapacity: zeroIOPS(),
    purchasePrice: 500,
    sellPrice: 250,
    costPerCycle: 2,
    outputConnectorLimits: {},
    blockSideEffects: (block: Block, iops: IOPS) => lambdaSideEffects(block, iops),
  },
  [`${BlockTypes.QUEUE}`]: {
    displayName: "Queue",
    imagePath: "",
    isInitiator: true,
    isTerminator: true,
    isInterceptor: false,
    allowedInputTypes: new Set([ BlockTypes.AUTO_SCALING_GROUP, BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.LAMBDA]),
    allowedOutputTypes: new Set([BlockTypes.APP_SERVER, BlockTypes.LAMBDA, BlockTypes.AUTO_SCALING_GROUP]),
    inputCapacity: {
      total: QUEUE_MAX_STORAGE, // High input capacity to accept bursts
      byOperationType: {
        [`${OperationTypes.WRITE}`]: {
          total: QUEUE_MAX_STORAGE,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: QUEUE_MAX_STORAGE },
        },
      }
    },
    processingCapacity: zeroIOPS(), // Delegates processing logic to the game loop
    purchasePrice: 200,
    sellPrice: 100,
    costPerCycle: 0.5,
    outputConnectorLimits: {},
  },
};



export const starterBlocks = [
  BlockTypes.APP_SERVER,
  BlockTypes.DATABASE,
  BlockTypes.DB_READ_REPLICA,
  BlockTypes.LOAD_BALANCER,
  BlockTypes.CDN,
  BlockTypes.AUTO_SCALING_GROUP,
  BlockTypes.CACHE,
  BlockTypes.LAMBDA,
  BlockTypes.QUEUE,
];

export function getBlockConfig(blockType: BlockTypes) {
  return blockConfigs[blockType];
}

//A functtion that multiplies the IOPS object by the given multiplier
export function multiplyIOPS(iops: IOPS, multiplier: number): IOPS {
  const result: IOPS = {
    ...zeroIOPS(),
    total: iops.total * multiplier
  };

  //loop over all operation types and multiply each value by the multiplier
  Object.keys(iops.byOperationType).forEach((opTypeKey: string) => {
    const opType = iops.byOperationType[`${opTypeKey}`];
    if (opType) {
      //loop over all content types
      let operationTotal = 0;
      Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
        //get current request count
        const contentTypeIOP = opType.byContentType[contentTypeKey];
        if (contentTypeIOP !== undefined && contentTypeIOP !== null) {
          //multiply the value by the multiplier
          const nextVal = contentTypeIOP * multiplier;
          //update content level counts 
          const nextContentIOP: ContentIOPSCountMap = {
            ...result.byOperationType[`${opTypeKey}`].byContentType,
            [`${contentTypeKey}`]: nextVal,
          };

          result.byOperationType[`${opTypeKey}`] = {
            ...result.byOperationType[`${opTypeKey}`],
            byContentType: nextContentIOP,
          };

          operationTotal += nextVal;
        }
      });

      //update operation level total
      result.byOperationType[`${opTypeKey}`] = {
        ...result.byOperationType[`${opTypeKey}`],
        total: operationTotal,
      };
    }
  });

  return result;
}

//A function that flattens the IOPS object by removing the byOperationType and byContentType properties
//and returning a flat map of operation types to their total IOPS
export const flattenIOPS = (iops: IOPS): FlattendIOPS => {
  const flatIOPS: FlattendIOPS = { total: iops.total };
  Object.keys(iops.byOperationType).forEach((opTypeKey: string) => {
    const opType = iops.byOperationType[`${opTypeKey}`];
    if (opType) {
      iops.byOperationType[`${opTypeKey}`].byContentType &&
        Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
          const contentTypeIOP = opType.byContentType[contentTypeKey];
          if (contentTypeIOP !== undefined && contentTypeIOP !== null) {
            //add the content type IOPS to the flat map
            flatIOPS[`${opTypeKey}-${contentTypeKey}`] = contentTypeIOP;
          }
        });

    }
  });
  return flatIOPS;
};

export const autoScalingGroupSideEffects = (block: Block, iops: IOPS): Block => {
  // Make a copy of the block to avoid mutating the original
  let finalBlock = { ...block };

  let finalDynamicProperties = finalBlock.dynamicProperties || { 'server-count': 1 };

  // Flatten the IOPS to get all operation-content type pairs
  const flatIOPS = flattenIOPS(iops);
  const flatCapacity = flattenIOPS(finalBlock.inputCapacity);

  // Collect all usage ratios for each IOPS type (excluding 'total')
  const usageRatios: number[] = [];
  Object.keys(flatIOPS).forEach(key => {
    if (key === 'total') return;
    const usage = flatIOPS[key];
    const capacity = flatCapacity[key] || 0;
    if (capacity > 0) {
      usageRatios.push(usage / capacity);
    }
  });

  // Determine scale up/down logic
  let shouldScaleUp = usageRatios.some(ratio => ratio > AUTO_SCALING_GROUP_SCALE_UP_THRESHOLD);
  let shouldScaleDown = usageRatios.length > 0 && usageRatios.every(ratio => ratio < AUTO_SCALING_GROUP_SCALE_DOWN_THRESHOLD);

  // Scale up if any usage is above the up threshold
  if (shouldScaleUp) {
    const currentServerCount = finalDynamicProperties['server-count'] || 1;
    finalDynamicProperties['server-count'] = currentServerCount + 1;
  }
  // Scale down if all usages are below the down threshold
  else if (shouldScaleDown) {
    const currentServerCount = finalDynamicProperties['server-count'] || 1;
    if (currentServerCount > 1) {
      finalDynamicProperties['server-count'] = currentServerCount - 1;
    }
  }

  // Update the block's dynamic properties
  finalBlock.dynamicProperties = finalDynamicProperties;

  const blockConfig = blockConfigs[finalBlock.blockType];
  const inputCapacity = blockConfig.inputCapacity;
  const processingCapacity = blockConfig.processingCapacity;

  // Update the input and processing capacities based on the new server count
  const serverCount = finalDynamicProperties['server-count'] || 1;
  finalBlock.inputCapacity = multiplyIOPS(inputCapacity, serverCount);
  finalBlock.processingCapacity = multiplyIOPS(processingCapacity, serverCount);

  return finalBlock;
};

// new helper: element-wise minimum of two IOPS objects (keeps structure of IOPS)
export function minIOPS(iops1: IOPS, iops2: IOPS): IOPS {
  const result: IOPS = {
    ...zeroIOPS(),
    total: Math.min(iops1.total || 0, iops2.total || 0),
  };

  Object.keys(iops1.byOperationType).forEach((opTypeKey: string) => {
    const opType1 = iops1.byOperationType[`${opTypeKey}`];
    const opType2 = iops2.byOperationType ? iops2.byOperationType[`${opTypeKey}`] : undefined;
    if (!opType1) return;

    let operationTotal = 0;
    Object.keys(opType1.byContentType).forEach((contentTypeKey: string) => {
      const val1 = opType1.byContentType[contentTypeKey] || 0;
      const val2 = (opType2 && opType2.byContentType && opType2.byContentType[contentTypeKey]) ? opType2.byContentType[contentTypeKey] : 0;
      const nextVal = Math.min(val1, val2);

      result.byOperationType[`${opTypeKey}`].byContentType[contentTypeKey] = nextVal;
      operationTotal += nextVal;
    });

    result.byOperationType[`${opTypeKey}`] = {
      ...result.byOperationType[`${opTypeKey}`],
      total: operationTotal,
    };
  });

  return result;
}

// Side effects for Cache blocks: start with zero processing capacity and increase (only) to match last seen IOPS up to maxProcessingCapacity
export const cacheSideEffects = (block: Block, iops: IOPS): Block => {
  let finalBlock = { ...block };

  // Ensure dynamic properties exist
  let finalDynamicProperties = finalBlock.dynamicProperties || {};

  // Determine the configured max processing capacity
  const config = blockConfigs[finalBlock.blockType];
  const maxProcFromConfig = finalBlock.maxProcessingCapacity || config?.maxProcessingCapacity || config?.processingCapacity || zeroIOPS();

  // Desired processing capacity should try to match the last seen input IOPS
  const desired = iops || zeroIOPS();

  // Compute the allowed processing capacity (element-wise min of desired and max)
  const allowed = minIOPS(desired, maxProcFromConfig);

  // Only increase processingCapacity (do not decrease automatically)
  const currentTotal = finalBlock.processingCapacity?.total || 0;
  const allowedTotal = allowed.total || 0;
  const maxTotal = maxProcFromConfig.total || 0;

  //If approaching max for the first time, set to max
  if (currentTotal >= maxTotal && maxTotal > 0 && finalDynamicProperties?.atMax !== true) {
    finalBlock.processingCapacity = maxProcFromConfig;
    finalDynamicProperties = { ...finalDynamicProperties, atMax: true }
  }
  else if (currentTotal >= maxTotal && maxTotal > 0 && finalDynamicProperties?.atMax === true) {
    //If already at max, randomly set to 75-100% of max on each call
    const multiplier = 0.75 + Math.random() * 0.25;
    finalBlock.processingCapacity = multiplyIOPS(maxProcFromConfig, multiplier);
    finalDynamicProperties = { ...finalDynamicProperties, atMax: false }
  }
  else if (allowedTotal > currentTotal) {
    finalBlock.processingCapacity = allowed;
  }

  finalBlock.dynamicProperties = finalDynamicProperties;
  finalBlock.maxProcessingCapacity = maxProcFromConfig;
  return finalBlock;
}
export const lambdaSideEffects = (block: Block, iops: IOPS): Block => {
  let finalBlock = { ...block };
  let finalDynamicProperties = finalBlock.dynamicProperties || { 'scale-count': 1 };

  // Base capacity per instance (defined in config)
  const baseConfig = blockConfigs[BlockTypes.LAMBDA];
  const baseCapacityTotal = baseConfig.inputCapacity.total;

  // Calculate required scale based on incoming total IOPS
  // We want enough capacity to handle the load.
  // If load is 0, we still keep 1 instance 'warm' or 0 if we want true serverless cold start simulation,
  // but for now let's say minimum 1 to avoid divide by zero or complexity.
  // Actually, let's allow it to scale up to meet demand.

  const currentLoad = iops.total;

  // Calculate needed instances. 
  // e.g. if load is 2500 and base is 1000, we need ceil(2.5) = 3 instances.
  let scaleCount = Math.ceil(currentLoad / baseCapacityTotal);
  if (scaleCount < 1) scaleCount = 1; // Minimum 1 instance

  finalDynamicProperties['scale-count'] = scaleCount;
  finalBlock.dynamicProperties = finalDynamicProperties;

  // Update capacities
  finalBlock.inputCapacity = multiplyIOPS(baseConfig.inputCapacity, scaleCount);
  finalBlock.processingCapacity = multiplyIOPS(baseConfig.processingCapacity, scaleCount);

  // Update cost per cycle
  // Cost scales linearly with the number of instances
  finalBlock.costPerCycle = baseConfig.costPerCycle * scaleCount;

  return finalBlock;
};
